import Vue from 'vue';
import Component from 'vue-class-component';
import { registerModule, unregisterModule, useModule } from 'vuex-simple';
import sanitizeHtml from 'sanitize-html';
import { EventEmitter } from 'events';
import { addSaga, sagaDispatch } from 'vuex-coolstory';

import { VueClass } from 'vue-class-component/lib/declarations';

import WithRender from './MainContainer.template';
import { getTagName } from './utils';

import { createNotificationAction } from '@/Framework/app/store/actions/creators';
import { EVENTS, MESSAGE_TYPES } from '@/Common/Runtime/events';
import { FINISH_NAVIGATION } from '@/Framework/app/store/sagas/actionsTypes';

import ViewportWrapper from '@/HostApp/ui/system/ViewportWrapper.vue';
import systemLayoutModule from '@/HostApp/services/SystemLayout/SystemLayoutModule';
import cssBundlerModule from '@/HostApp/services/CSSBundler/CSSBundlerModule';
import spriteBundlerModule from '@/HostApp/services/CSSBundler/SpriteBundlerModule';

import aps from '@/HostApp/store/ApsModules/ApsModule';

import { Prop } from 'vue-property-decorator';
import { findAttrByName } from '@/Common/Utils/General/Modules/translate';

const STATE_SUFIX = '-state';

const getMainContainer = ({
	config,
	instanceModule,
	homeRoute,
	emitter,
	sharedComponents = {},
	start,
	saga
}: App.IParamsMainContainer): VueClass<App.IMainContainer> => {
	@WithRender
	@Component<MainContainer>({
		watch: {
			currentRoute(newCurrentRoute: string, oldCurrentRoute: string): void {
				!config.isDev && newCurrentRoute && this.updateCurrentRoute(newCurrentRoute, oldCurrentRoute);
			}
		},
		components: {
			ViewportWrapper
		}
	})
	class MainContainer extends Vue implements App.IMainContainer {
		@Prop()
		public layoutTemplate?: LAYOUT.ILayoutTemplate;

		public isReady = false;
		protected store: App.IRootModule | null = null;
		protected version: string = config.version || 'none';
		protected isDev: boolean = config.isDev;
		public nameApp: string = config.nameApp || 'Unknown app name';
		protected emitter: EventEmitter = emitter;
		protected sharedComponents: Common.IMap<VueClass<Vue>> = sharedComponents;
		protected config: App.IConfig = config;
		protected homeRoute: App.IRoute = homeRoute;
		protected stylesheets: string[] = [];
		protected isVisibleRoute: boolean = false;

		protected get storeName(): string {
			return this.nameApp + STATE_SUFIX;
		}

		public get router(): App.IRouteModule {
			// TODO Remove in future
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return this.store?.router || (this.store as any)?.route;
		}

		protected get currentRoute(): string {
			const route = this.router?.currentRoute || '';

			return sanitizeHtml(route);
		}

		protected get currentTagName(): string {
			return getTagName(this.currentRoute);
		}

		protected get routeParams(): string {
			return this.router.routeParams;
		}

		protected get currentTagOrComponentName(): string {
			const { isDev, currentRoute, currentTagName } = this;
			const { customElement } = this.$root.$options as TRANSLATE.ICustomOptions;

			const packageId = `packageid="${findAttrByName(customElement, 'packageId')}"`;
			const currentTag = currentRoute && `<${currentTagName} ${packageId} />`;
			return isDev ? currentRoute : currentTag;
		}

		protected get isVueWorkspace(): boolean {
			return aps.navigation.isVueWorkspace;
		}

		protected get computedContainerStyles(): Partial<CSSStyleDeclaration> {
			return {};
		}

		protected get notifications(): Common.IMap<App.INoty> {
			return this.store?.common.notifications.notifications || {};
		}

		protected created(): void {
			const { id: fullId } = aps.context._view;
			const [id] = fullId.split('#');

			const { layoutTemplate } = this;
			const layoutService = systemLayoutModule;

			if (id && layoutTemplate) {
				layoutService.registerLayoutTemplate({ id, layoutTemplate });
			}

			this.registerStore();
			this.handshake();
			this.addListeners();

			this.$watch('routeParams', () => {
				return this.applyCSSandSprite();
			});

			start && start();
		}

		protected mounted(): void {
			if (this.isDev) {
				this.isReady = true;
			}
		}

		protected destroyed(): void {
			const { storeName } = this;

			unregisterModule(this.$store, [storeName]);
		}

		protected destroyedWebComponent(oldCurrentRoute: string): void {
			const { $el, $ux1IsHostDevMode: isDev } = this;

			if ($el) {
				const tagName = getTagName(oldCurrentRoute);
				const oldWC = $el.querySelector(tagName) as Host.WCElementWithDeactivated;

				if (oldWC) {
					const { vueComponent } = oldWC;

					if (vueComponent?.$options) {
						vueComponent.$options.deactivated = [vueComponent.$destroy];
					} else if (isDev) {
						const message = `[UX1FW] Instance Vue not found: route:${oldCurrentRoute}, webcomponent: '${tagName}'`;

						// eslint-disable-next-line no-console
						console.warn(message);
					}
				} else if (isDev) {
					const message = `[UX1FW] Destroyed route:${oldCurrentRoute} - Not found webcomponent: '${tagName}'`;

					// eslint-disable-next-line no-console
					console.warn(message);
				}
			}
		}

		protected async applyCSSandSprite(): Promise<void> {
			if (cssBundlerModule && spriteBundlerModule) {
				this.isVisibleRoute = false;

				await this.$nextTick();

				const elTag = this.$el.querySelector(this.currentTagName) as HTMLElement;

				await cssBundlerModule.applyCSSBundles(elTag.shadowRoot, this.stylesheets);
				await spriteBundlerModule.attachSprite(elTag.shadowRoot);

				this.isVisibleRoute = true;
			}
		}

		protected async updateCurrentRoute(currentRoute: string, oldCurrentRoute: string): Promise<void> {
			oldCurrentRoute && this.destroyedWebComponent(oldCurrentRoute);

			await this.applyCSSandSprite();
		}

		protected registerStore(): void {
			const { storeName } = this;

			registerModule(this.$store, [storeName], instanceModule);
			this.store = useModule<App.IRootModule>(this.$store, [storeName]) || null;

			if (!this.store) {
				throw new Error('The application store module cannot be used. Work is impossible!');
			} else {
				saga && addSaga(saga, this.$store);
			}
		}

		protected addListeners(): void {
			// Send message from app to host
			this.emitter.addListener(EVENTS.PRIVATE.TO_HOST, (payload: App.IPayloadEmit) => {
				const { nameApp: name, currentRoute: viewName } = this;

				this.$emit(EVENTS.PUBLIC.TO_HOST, {
					...payload,
					app: {
						name,
						viewName
					}
				});
			});

			// Receiving message from host
			this.emitter.addListener(EVENTS.PRIVATE.FROM_HOST, (payloadData: App.IPayloadEmit) => {
				const { type, data } = payloadData;

				if (type === MESSAGE_TYPES.NAVIGATION) {
					if (this.router?.processing) {
						sagaDispatch(FINISH_NAVIGATION, { data });
					} else {
						const { data = {} } = payloadData;
						const { viewName: route, payload } = data;

						route && this.router?.callUpdateRoute({ route, payload });
					}
				}

				this.emitter.emit(EVENTS.PUBLIC.FROM_HOST, payloadData);
			});
		}

		protected handshake(): void {
			this.$emit('ready');

			const unwatch = this.$watch('isReady', (isReady) => {
				if (isReady && this.$store) {
					this.router?.callUpdateRoute({ ...this.homeRoute });

					unwatch();
				}
			});
		}

		protected notify(notification: App.INoty): void {
			if (this.isDev) {
				const type = notification?.type || 'error';

				this.$notify[type] && this.$notify[type](notification);
			}

			this.emitter.emit(EVENTS.PRIVATE.TO_HOST, createNotificationAction(notification));
		}

		protected updateNotification(type: string, id: string): void {
			if (type === 'add') {
				const notification = this.notifications[id];

				this.notify(notification);
				this.store?.common.notifications.remove(id);
			}
		}

		private setHomeRoute(homeRoute: App.IRoute): void {
			this.homeRoute = homeRoute;
		}

		private setStylesheets(stylesheets: string[]): void {
			this.stylesheets = stylesheets;
		}

		// Send message from host to app
		public emit(payload: App.IPayloadData): void {
			this.emitter.emit(EVENTS.PRIVATE.FROM_HOST, payload);
		}

		public setOptions(options: Partial<Host.IOptionsCreateApp> = {}): void {
			const { homeRoute, packageId, stylesheetUrls, baseApiUrl, context } = options;
			stylesheetUrls && this.setStylesheets(stylesheetUrls);
			homeRoute && this.setHomeRoute(homeRoute);
			packageId && config.setPackageId(packageId);
			baseApiUrl && config.setBaseApiUrl(baseApiUrl);
			context && config.setContext && config.setContext(context);
		}

		public getComponent(name: string): VueClass<Vue> | null {
			return this.sharedComponents[name] || null;
		}
	}

	return MainContainer;
};

export default getMainContainer;
